import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERVICES } from './services';
import { CALL_URL } from './Legal';

// One enquiry form used on every page (landing page, service pages, Strategy Automation page).
// It collects the same fields everywhere, so every lead reaches the Leads sheet and Qire Agency OS complete,
// and the visitor chooses how to share the details: a document or recording after an NDA, a call, or in writing.
//
// The option lists below MUST match the lists in the workbook (Lists tab, columns "BUDGET (WEBSITE)", "TIMELINE (WEBSITE)",
// "ROLE (WEBSITE)", "PLATFORM (WEBSITE)"). Change them in both places or not at all.

export const BUDGET = ['Under 300', '300 to 800', '800 to 2,000', 'Over 2,000'];
export const TIMELINE = ['As soon as possible', 'Within a month', 'Within three months', 'Just exploring'];
export const ROLE = ['Strategy owner', 'Trading educator', 'Signal provider', 'Broker or IB', 'Fintech team', 'Other'];
export const PLATFORM = ['MetaTrader 5', 'MetaTrader 4', 'Other', 'Not sure'];

// The label is what lands in the sheet (Code.gs reads it), so keep the wording of the three labels in step with Code.gs.
const SHARE = [
  { id: 'doc', title: 'I have it written down or recorded', hint: 'A PDF, document or video of your strategy or requirements. We email you an NDA first, and you share the file after you sign.', button: 'Send details', label: 'Has it written down or recorded (PDF or video); send after NDA' },
  { id: 'call', title: 'I would rather talk it through on a call', hint: 'Send your details first, then pick a time on our calendar.', button: 'Send details and book a call', label: 'Wants a call' },
  { id: 'chat', title: 'I will explain it in writing', hint: 'Describe it in a few sentences. We reply by email or text message, whichever you pick.', button: 'Send details', label: 'Will explain in writing (email or text)' },
];
const REPLY = ['Email', 'Text message'];
const NO_CALL_TIMELINE = 'Just exploring';   // a call is for projects that are ready to start; the written options stay open
const NOT_SURE = 'Other or not sure';

const CSS = `
.lf{--lf-ink:var(--white);--lf-muted:var(--white-dimmer);--lf-line:var(--border);--lf-field:var(--surface-2);--lf-accent:var(--teal);--lf-on:#050506;--lf-bad:#d64545;display:grid;gap:1.1rem;font-family:inherit;color:var(--lf-ink)}
.lf.ea{--lf-ink:var(--ink);--lf-muted:var(--muted);--lf-line:var(--border);--lf-field:var(--paper);--lf-accent:var(--teal);--lf-on:var(--on-teal,#fff);font:inherit}
.lf *{box-sizing:border-box}
.lf-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
.lf-g{display:grid;gap:.35rem;align-content:start}
.lf-l{font-size:.78rem;letter-spacing:.08em;text-transform:uppercase;color:var(--lf-muted);font-weight:600}
.lf-l i{font-style:normal;text-transform:none;letter-spacing:0;font-weight:400}
.lf-c{font:inherit;font-size:.95rem;color:var(--lf-ink);background:var(--lf-field);border:1px solid var(--lf-line);border-radius:6px;padding:.7rem .85rem;width:100%;outline:none;min-width:0;min-height:46px}
.lf-c:focus{border-color:var(--lf-accent)}
.lf-c[aria-invalid=true]{border-color:var(--lf-bad)}
textarea.lf-c{min-height:110px;resize:vertical}
select.lf-c{cursor:pointer}
.lf-err{font-size:.82rem;color:var(--lf-bad);margin:0}
.lf-note{font-size:.85rem;color:var(--lf-muted);margin:0;line-height:1.5}
.lf-h{font-size:.78rem;letter-spacing:.08em;text-transform:uppercase;color:var(--lf-muted);font-weight:600;margin:.2rem 0 -.3rem}
.lf-opts{display:grid;gap:.6rem}
.lf-opt{display:flex;gap:.75rem;align-items:flex-start;text-align:left;font:inherit;color:var(--lf-ink);background:var(--lf-field);border:1px solid var(--lf-line);border-radius:8px;padding:.85rem 1rem;cursor:pointer;width:100%}
.lf-opt:hover:not(:disabled){border-color:var(--lf-accent)}
.lf-opt[aria-checked=true]{border-color:var(--lf-accent);box-shadow:0 0 0 1px var(--lf-accent)}
.lf-opt:disabled{opacity:.5;cursor:not-allowed}
.lf-dot{flex:0 0 auto;width:16px;height:16px;border-radius:50%;border:2px solid var(--lf-line);margin-top:3px;position:relative}
.lf-opt[aria-checked=true] .lf-dot{border-color:var(--lf-accent)}
.lf-opt[aria-checked=true] .lf-dot::after{content:'';position:absolute;inset:2px;border-radius:50%;background:var(--lf-accent)}
.lf-opt b{display:block;font-weight:600;font-size:.95rem}
.lf-opt span{display:block;font-size:.85rem;color:var(--lf-muted);line-height:1.5;margin-top:2px}
.lf-chips{display:flex;gap:.5rem;flex-wrap:wrap}
.lf-chip{font:inherit;font-size:.85rem;color:var(--lf-ink);background:var(--lf-field);border:1px solid var(--lf-line);border-radius:99px;padding:.35rem .9rem;cursor:pointer}
.lf-chip[aria-checked=true]{border-color:var(--lf-accent);color:var(--lf-accent);font-weight:600}
.lf-chk{display:flex;gap:.6rem;align-items:flex-start;font-size:.85rem;color:var(--lf-muted);line-height:1.5}
.lf-chk input{margin-top:.2rem}
.lf-chk a{color:var(--lf-accent);text-decoration:underline}
.lf-btn{justify-self:start;font:inherit;font-weight:600;font-size:.95rem;background:var(--lf-accent);color:var(--lf-on);border:0;border-radius:6px;padding:.85rem 1.6rem;cursor:pointer}
.lf-btn:disabled{opacity:.6;cursor:wait}
.lf-hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}
.lf-done{padding:1.6rem;border:1px solid var(--lf-accent);border-radius:8px;display:grid;gap:.8rem;background:var(--lf-field)}
.lf-done h3{font-size:1.1rem;margin:0}
.lf-done p{margin:0;color:var(--lf-muted);line-height:1.6;font-size:.95rem}
.lf-done a.lf-btn{text-decoration:none;display:inline-block}
.lf-phone{display:flex;gap:.5rem;align-items:stretch}
.lf-phone>input{flex:1 1 auto;min-width:0}
.lf-cc-wrap{position:relative;flex:0 0 auto}
.lf .lf-cc{display:flex;align-items:center;gap:.5rem;width:auto;height:100%;cursor:pointer;white-space:nowrap;text-align:left}
.lf-cc-arrow{font-size:.7rem;opacity:.7}
.lf-flag{display:block;width:22px;height:16px;object-fit:cover;border-radius:2px;box-shadow:0 0 0 1px rgba(128,128,140,.35);flex:0 0 auto}
.lf-panel{position:absolute;z-index:30;top:calc(100% + 6px);left:0;width:min(320px,86vw);background:var(--lf-field);border:1px solid var(--lf-line);border-radius:8px;padding:.5rem;box-shadow:0 18px 40px -12px rgba(0,0,0,.55)}
.lf-search{margin-bottom:.4rem}
.lf-list{list-style:none;margin:0;padding:0;max-height:240px;overflow-y:auto;overscroll-behavior:contain}
.lf-copt{display:flex;align-items:center;gap:.6rem;padding:.55rem .5rem;border-radius:6px;cursor:pointer;font-size:.9rem;line-height:1.3}
.lf-copt:hover,.lf-copt.on{background:rgba(128,128,140,.16)}
.lf-copt-name{flex:1 1 auto;min-width:0}
.lf-copt-dial{opacity:.65;font-size:.85rem}
.lf-none{padding:.6rem .5rem;font-size:.9rem;opacity:.7}
@media(max-width:600px){.lf-row{grid-template-columns:1fr}.lf-c{font-size:16px}.lf-btn{justify-self:stretch}}
`;

// Country calling codes for the phone selector. Flags are small images from flagcdn.com (Windows does not draw emoji flags).
const COUNTRIES = `AF|Afghanistan|93;AL|Albania|355;DZ|Algeria|213;AS|American Samoa|1684;AD|Andorra|376;AO|Angola|244;AI|Anguilla|1264;AG|Antigua and Barbuda|1268;AR|Argentina|54;AM|Armenia|374;AW|Aruba|297;AU|Australia|61;AT|Austria|43;AZ|Azerbaijan|994;BS|Bahamas|1242;BH|Bahrain|973;BD|Bangladesh|880;BB|Barbados|1246;BY|Belarus|375;BE|Belgium|32;BZ|Belize|501;BJ|Benin|229;BM|Bermuda|1441;BT|Bhutan|975;BO|Bolivia|591;BA|Bosnia and Herzegovina|387;BW|Botswana|267;BR|Brazil|55;VG|British Virgin Islands|1284;BN|Brunei|673;BG|Bulgaria|359;BF|Burkina Faso|226;BI|Burundi|257;KH|Cambodia|855;CM|Cameroon|237;CA|Canada|1;CV|Cape Verde|238;KY|Cayman Islands|1345;CF|Central African Republic|236;TD|Chad|235;CL|Chile|56;CN|China|86;CO|Colombia|57;KM|Comoros|269;CG|Congo|242;CD|DR Congo|243;CR|Costa Rica|506;CI|Cote d'Ivoire|225;HR|Croatia|385;CU|Cuba|53;CW|Curacao|599;CY|Cyprus|357;CZ|Czechia|420;DK|Denmark|45;DJ|Djibouti|253;DM|Dominica|1767;DO|Dominican Republic|1809;EC|Ecuador|593;EG|Egypt|20;SV|El Salvador|503;GQ|Equatorial Guinea|240;ER|Eritrea|291;EE|Estonia|372;SZ|Eswatini|268;ET|Ethiopia|251;FJ|Fiji|679;FI|Finland|358;FR|France|33;GA|Gabon|241;GM|Gambia|220;GE|Georgia|995;DE|Germany|49;GH|Ghana|233;GI|Gibraltar|350;GR|Greece|30;GL|Greenland|299;GD|Grenada|1473;GU|Guam|1671;GT|Guatemala|502;GN|Guinea|224;GW|Guinea-Bissau|245;GY|Guyana|592;HT|Haiti|509;HN|Honduras|504;HK|Hong Kong|852;HU|Hungary|36;IS|Iceland|354;IN|India|91;ID|Indonesia|62;IR|Iran|98;IQ|Iraq|964;IE|Ireland|353;IL|Israel|972;IT|Italy|39;JM|Jamaica|1876;JP|Japan|81;JO|Jordan|962;KZ|Kazakhstan|7;KE|Kenya|254;KI|Kiribati|686;XK|Kosovo|383;KW|Kuwait|965;KG|Kyrgyzstan|996;LA|Laos|856;LV|Latvia|371;LB|Lebanon|961;LS|Lesotho|266;LR|Liberia|231;LY|Libya|218;LI|Liechtenstein|423;LT|Lithuania|370;LU|Luxembourg|352;MO|Macao|853;MG|Madagascar|261;MW|Malawi|265;MY|Malaysia|60;MV|Maldives|960;ML|Mali|223;MT|Malta|356;MH|Marshall Islands|692;MR|Mauritania|222;MU|Mauritius|230;MX|Mexico|52;FM|Micronesia|691;MD|Moldova|373;MC|Monaco|377;MN|Mongolia|976;ME|Montenegro|382;MS|Montserrat|1664;MA|Morocco|212;MZ|Mozambique|258;MM|Myanmar|95;NA|Namibia|264;NR|Nauru|674;NP|Nepal|977;NL|Netherlands|31;NZ|New Zealand|64;NI|Nicaragua|505;NE|Niger|227;NG|Nigeria|234;KP|North Korea|850;MK|North Macedonia|389;NO|Norway|47;OM|Oman|968;PK|Pakistan|92;PW|Palau|680;PS|Palestine|970;PA|Panama|507;PG|Papua New Guinea|675;PY|Paraguay|595;PE|Peru|51;PH|Philippines|63;PL|Poland|48;PT|Portugal|351;PR|Puerto Rico|1787;QA|Qatar|974;RO|Romania|40;RU|Russia|7;RW|Rwanda|250;KN|Saint Kitts and Nevis|1869;LC|Saint Lucia|1758;VC|Saint Vincent and the Grenadines|1784;WS|Samoa|685;SM|San Marino|378;ST|Sao Tome and Principe|239;SA|Saudi Arabia|966;SN|Senegal|221;RS|Serbia|381;SC|Seychelles|248;SL|Sierra Leone|232;SG|Singapore|65;SK|Slovakia|421;SI|Slovenia|386;SB|Solomon Islands|677;SO|Somalia|252;ZA|South Africa|27;KR|South Korea|82;SS|South Sudan|211;ES|Spain|34;LK|Sri Lanka|94;SD|Sudan|249;SR|Suriname|597;SE|Sweden|46;CH|Switzerland|41;SY|Syria|963;TW|Taiwan|886;TJ|Tajikistan|992;TZ|Tanzania|255;TH|Thailand|66;TL|Timor-Leste|670;TG|Togo|228;TO|Tonga|676;TT|Trinidad and Tobago|1868;TN|Tunisia|216;TR|Turkiye|90;TM|Turkmenistan|993;TC|Turks and Caicos|1649;TV|Tuvalu|688;UG|Uganda|256;UA|Ukraine|380;AE|United Arab Emirates|971;GB|United Kingdom|44;US|United States|1;UY|Uruguay|598;UZ|Uzbekistan|998;VU|Vanuatu|678;VA|Vatican City|379;VE|Venezuela|58;VN|Vietnam|84;YE|Yemen|967;ZM|Zambia|260;ZW|Zimbabwe|263`.split(';').map((x) => { const p = x.split('|'); return { iso: p[0], name: p[1], dial: p[2] }; });

const DEFAULT_ISO = 'US';
const flagUrl = (iso) => `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;

function Flag({ iso }) {
  return <img className="lf-flag" src={flagUrl(iso)} alt="" width="22" height="16" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />;
}

/** Country code picker (flag and +code) with a search box. Keyboard and touch friendly. */
function CountryPicker({ iso, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  const cur = COUNTRIES.find((x) => x.iso === iso) || COUNTRIES.find((x) => x.iso === DEFAULT_ISO);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('touchstart', away);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('touchstart', away); };
  }, [open]);
  const needle = q.trim().toLowerCase().replace(/^\+/, '');
  const list = needle ? COUNTRIES.filter((x) => x.name.toLowerCase().includes(needle) || x.dial.startsWith(needle) || x.iso.toLowerCase() === needle) : COUNTRIES;
  const pick = (x) => { onPick(x.iso); setOpen(false); setQ(''); };
  return (
    <div className="lf-cc-wrap" ref={box} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <button type="button" className="lf-c lf-cc" aria-haspopup="listbox" aria-expanded={open} aria-label={`Country code, ${cur.name} +${cur.dial}`} onClick={() => setOpen(!open)}>
        <Flag iso={cur.iso} /><span>+{cur.dial}</span><span className="lf-cc-arrow" aria-hidden="true">&#9662;</span>
      </button>
      {open && (
        <div className="lf-panel">
          <input className="lf-c lf-search" autoFocus type="search" placeholder="Search country or code" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search country" />
          <ul role="listbox" className="lf-list">
            {list.length === 0 && <li className="lf-none">No match</li>}
            {list.map((x) => (
              <li key={x.iso} role="option" aria-selected={x.iso === cur.iso} className={'lf-copt' + (x.iso === cur.iso ? ' on' : '')} onClick={() => pick(x)}>
                <Flag iso={x.iso} /><span className="lf-copt-name">{x.name}</span><span className="lf-copt-dial">+{x.dial}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
const digits = (s) => String(s || '').replace(/\D/g, '');

// --- checks that keep out keyboard-mashing, without turning real people away ---
const VOWEL = /[aeiouyáéíóúàèìòùäëïöüâêîôû]/i;
const looksMashed = (w) => {
  const x = w.replace(/[^\p{L}]/gu, '');
  if (x.length < 4) return false;
  if (!VOWEL.test(x)) return true;
  if (/[^aeiouyáéíóúàèìòùäëïöüâêîôû\s]{5,}/i.test(x)) return true;   // five or more consonants in a row
  if (/(.)\1{3,}/.test(x)) return true;                               // aaaa
  return false;
};
const goodName = (s) => {
  const v = s.trim();
  if (v.length < 2 || !/^[\p{L}][\p{L} '.-]*$/u.test(v)) return false;
  return !v.split(/[\s'.-]+/).some(looksMashed);
};
const DISPOSABLE = /@(mailinator|guerrillamail|10minutemail|tempmail|temp-mail|yopmail|trashmail|sharklasers|getnada|throwawaymail|dispostable)\./i;
const goodEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim()) && !DISPOSABLE.test(s);
const goodMessage = (s) => {
  const v = s.trim(); const words = v.split(/\s+/).filter(Boolean);
  const letters = (v.match(/\p{L}/gu) || []).length;
  if (v.length < 40 || words.length < 6 || letters / v.replace(/\s/g, '').length < 0.7) return false;
  return words.filter((w) => looksMashed(w)).length <= words.length * 0.3;
};

/** Full international number from the picked country and what was typed. Returns { phone, ok }. */
function buildPhone(typedRaw, iso) {
  const typed = clean(typedRaw);
  const dial = (COUNTRIES.find((x) => x.iso === iso) || COUNTRIES.find((x) => x.iso === DEFAULT_ISO)).dial;
  const intl = typed.startsWith('+');
  const national = intl ? digits(typed) : digits(typed).replace(/^0+/, '');        // a leading 0 is the local trunk prefix
  const phone = intl ? '+' + digits(typed) : '+' + dial + ' ' + typed.replace(/[^\d\s\-().]/g, '').replace(/^[\s0]+/, '').trim();
  const ok = national.length >= (intl ? 8 : 6) && digits(phone).length <= 15 && !/^(\d)\1+$/.test(national);
  return { phone, ok };
}

const EMPTY = { first: '', last: '', email: '', phone: '', iso: DEFAULT_ISO, service: '', role: '', platform: '', budget: '', timeline: '', share: '', reply: 'Email', notes: '', website: '', consent: false };

/**
 * Props
 *   onSubmitLead  the function from App.js
 *   variant       'q' (landing page styling) or 'ea' (service and strategy page styling)
 *   formName      who this form is: 'Landing page contact form', 'Service page: Trading bots & EAs', ...
 *   source        value for the lead's Source column: 'Website' | 'Service page' | 'Strategy Automation page'
 *   service       fixed service name (service pages and strategy page). Leave empty on the landing page to show the service picker.
 *   subject       legacy subject line (kept so older copies of the Google script still understand the lead)
 *   callUrl       booking link (opens only after the details have been sent)
 *   track         optional analytics function (name, props)
 */
export default function LeadForm({ onSubmitLead, variant = 'q', formName, source = 'Website', service = '', subject = '', callUrl = CALL_URL, track }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ ...EMPTY, service: service || '' });
  const [err, setErr] = useState({});
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const opened = useRef(Date.now());
  const root = useRef(null);
  useEffect(() => { opened.current = Date.now(); }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const noCall = f.timeline === NO_CALL_TIMELINE;
  const share = noCall && f.share === 'call' ? '' : f.share;
  const chosen = SHARE.find((s) => s.id === share);
  const cls = `lf ${variant === 'ea' ? 'ea' : ''}`;
  const serviceOptions = [...SERVICES.map((s) => s.name), 'Strategy Automation', NOT_SURE];

  const validate = () => {
    const e = {};
    if (!goodName(f.first)) e.first = 'Please enter your real first name.';
    if (!goodName(f.last)) e.last = 'Please enter your real last name.';
    if (!goodEmail(f.email)) e.email = 'Please enter a working email address.';
    if (!buildPhone(f.phone, f.iso).ok) e.phone = 'Please enter your phone number after the country code.';
    if (!f.service) e.service = 'Please choose what you need.';
    if (!f.role) e.role = 'Please choose one.';
    if (!f.platform) e.platform = 'Please choose a platform.';
    if (!f.budget) e.budget = 'Please choose a budget range.';
    if (!f.timeline) e.timeline = 'Please choose when you need it.';
    if (!share) e.share = 'Please choose how you would like to share the details.';
    if (share === 'chat' && !goodMessage(f.notes)) e.notes = 'Please describe what you need in a few real sentences (at least 40 characters).';
    if (!f.consent) e.consent = 'Please tick this box so we can reply to you.';
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (busy) return;
    const e = validate();
    setErr(e);
    if (Object.keys(e).length) {
      const el = root.current && root.current.querySelector(`[data-f="${Object.keys(e)[0]}"]`);
      if (el && el.focus) el.focus();
      return;
    }
    if (f.website || Date.now() - opened.current < 3500) { setDone({ share, first: clean(f.first), reply: f.reply }); return; }   // honeypot, or a bot that filled the form instantly: look successful, send nothing
    setBusy(true);
    const first = clean(f.first), last = clean(f.last);
    const { phone } = buildPhone(f.phone, f.iso);
    const utm = new URLSearchParams(window.location.search).get('utm_source') || 'direct';
    const reply = share === 'chat' ? f.reply : '';
    const lines = [
      `Service: ${f.service}`, `Phone: ${phone}`, `Role: ${f.role}`, `Platform: ${f.platform}`, `Budget: ${f.budget}`, `Timeline: ${f.timeline}`,
      `Sharing: ${chosen.label}`, reply && `Reply: ${reply}`, `Source: ${utm}`,
    ].filter(Boolean);
    const notes = f.notes.trim();
    if (onSubmitLead) onSubmitLead({
      type: 'lead', name: `${first} ${last}`, firstName: first, lastName: last, email: clean(f.email).toLowerCase(), phone,
      service: f.service, role: f.role, platform: f.platform, budget: f.budget, timeline: f.timeline,
      sharing: chosen.label, reply, subject: subject || f.service,
      message: [...lines, '', notes].join('\n'),
      source, form: formName || 'Website form', utm,
    });
    if (track) track('Lead Submit', { form: formName, share, budget: f.budget });
    if (share === 'call' && callUrl) window.open(callUrl, '_blank', 'noopener');   // same click, so the browser allows it; the button on the next screen covers a blocked pop-up
    setDone({ share, first, reply });
    setBusy(false);
  };

  if (done) {
    return (
      <div className={cls}><style>{CSS}</style>
        <div className="lf-done" role="status">
          <h3>Thank you{done.first ? ', ' + done.first : ''}. We have your details.</h3>
          {done.share === 'doc' && <p>Please keep your document or recording for now. We will email you a short NDA within 24 hours. Once it is signed, send the file and we will go through it with you.</p>}
          {done.share === 'call' && <><p>One more step: pick a time that suits you for the call. If the booking page did not open, use the button.</p>
            <a className="lf-btn" href={callUrl} target="_blank" rel="noopener noreferrer">Pick a time</a></>}
          {done.share === 'chat' && <p>We will reply within 24 hours {done.reply === 'Text message' ? 'by text message' : 'by email'} with questions or a fixed quote.</p>}
        </div>
      </div>
    );
  }

  const field = (k, label, input, hint) => (
    <div className="lf-g">
      <label className="lf-l" htmlFor={`lf-${k}`}>{label}</label>
      {input}
      {hint && !err[k] && <p className="lf-note">{hint}</p>}
      {err[k] && <p className="lf-err" role="alert">{err[k]}</p>}
    </div>
  );
  const inp = (k, props) => <input id={`lf-${k}`} data-f={k} className="lf-c" value={f[k]} onChange={set(k)} aria-invalid={!!err[k]} {...props} />;
  const sel = (k, opts) => (
    <select id={`lf-${k}`} data-f={k} className="lf-c" value={f[k]} onChange={set(k)} aria-invalid={!!err[k]}>
      <option value="">Select one</option>{opts.map((o) => <option key={o}>{o}</option>)}
    </select>
  );

  return (
    <form className={cls} onSubmit={submit} noValidate ref={root}>
      <style>{CSS}</style>
      <div className="lf-row">
        {field('first', 'First name', inp('first', { autoComplete: 'given-name', maxLength: 60 }))}
        {field('last', 'Last name', inp('last', { autoComplete: 'family-name', maxLength: 60 }))}
      </div>
      <div className="lf-row">
        {field('email', 'Email', inp('email', { type: 'email', autoComplete: 'email', placeholder: 'you@company.com', maxLength: 160 }))}
        {field('phone', 'Phone',
          <div className="lf-phone">
            <CountryPicker iso={f.iso} onPick={(iso) => setF((p) => ({ ...p, iso }))} />
            {inp('phone', { type: 'tel', inputMode: 'tel', autoComplete: 'tel-national', placeholder: 'Phone number', maxLength: 24 })}
          </div>)}
      </div>
      {!service && field('service', 'What do you need?', sel('service', serviceOptions))}
      <div className="lf-row">
        {field('role', 'I am a', sel('role', ROLE))}
        {field('platform', 'Platform', sel('platform', PLATFORM))}
      </div>
      <div className="lf-row">
        {field('budget', 'Budget (USD)', sel('budget', BUDGET))}
        {field('timeline', 'When do you need it?', sel('timeline', TIMELINE))}
      </div>

      <p className="lf-h">How would you like to share the details?</p>
      <div className="lf-opts" role="radiogroup" aria-label="How would you like to share the details?">
        {SHARE.map((s) => {
          const off = s.id === 'call' && noCall;
          return (
            <button type="button" key={s.id} role="radio" aria-checked={share === s.id} disabled={off} data-f={s.id === 'doc' ? 'share' : undefined}
              className="lf-opt" onClick={() => setF((p) => ({ ...p, share: s.id }))}>
              <i className="lf-dot" /><div><b>{s.title}</b><span>{off ? 'Calls are for projects that are ready to start. Choose one of the other options and we will reply.' : s.hint}</span></div>
            </button>
          );
        })}
      </div>
      {err.share && <p className="lf-err" role="alert">{err.share}</p>}

      {share === 'chat' && (
        <div className="lf-g">
          <span className="lf-l">Reply by</span>
          <div className="lf-chips" role="radiogroup" aria-label="Reply by">
            {REPLY.map((r) => <button type="button" key={r} role="radio" aria-checked={f.reply === r} className="lf-chip" onClick={() => setF((p) => ({ ...p, reply: r }))}>{r}</button>)}
          </div>
        </div>
      )}
      {share && field('notes', share === 'chat' ? 'Describe your project' : <>Anything else we should know? <i>(optional)</i></>,
        <textarea id="lf-notes" data-f="notes" className="lf-c" maxLength={1500} value={f.notes} onChange={set('notes')} aria-invalid={!!err.notes}
          placeholder={share === 'chat' ? 'What should it do, which markets, and what result do you want?' : ''} />)}

      <input className="lf-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={f.website} onChange={set('website')} />
      <div>
        <label className="lf-chk"><input type="checkbox" data-f="consent" checked={f.consent} onChange={set('consent')} />
          <span>I agree to my details being used to reply to this enquiry, as described in the <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy Policy</a>.</span></label>
        {err.consent && <p className="lf-err" role="alert">{err.consent}</p>}
      </div>
      <button type="submit" className="lf-btn" disabled={busy}>{chosen ? chosen.button : 'Send details'} →</button>
    </form>
  );
}
